/**
 * Typed binding for role assignment (PR #5a.4).
 *
 *   - listTemplates(query)        → the "add role" picker (templates + SoD pairs).
 *   - listForUser(userId)         → a user's current role chips.
 *   - assign(userId, body)        → grant a role.
 *   - unassign(userId, roleId)    → revoke a role.
 *
 * All require an authenticated session AND one of
 * `admin.users.global` / `admin.users.own_entity`. Wire errors that
 * surface here include `role_entity_type_mismatch`, `role_sod_conflict`,
 * `role_already_assigned`, `role_last_admin`, `not_found`, `forbidden`.
 */

import {
  RoleTemplateListResponseSchema,
  UserRolesResponseSchema,
  type RoleAssignRequest,
  type RoleTemplateListQuery,
  type RoleTemplateListResponse,
  type UserRolesResponse,
} from '../schemas/role.js';
import type { RequestOptions, Transport } from './transport.js';

export interface RolesClient {
  listTemplates(
    query?: RoleTemplateListQuery,
    options?: RequestOptions,
  ): Promise<RoleTemplateListResponse>;
  listForUser(userId: string, options?: RequestOptions): Promise<UserRolesResponse>;
  assign(
    userId: string,
    body: RoleAssignRequest,
    options?: RequestOptions,
  ): Promise<UserRolesResponse>;
  unassign(
    userId: string,
    roleTemplateId: string,
    options?: RequestOptions,
  ): Promise<UserRolesResponse>;
}

const buildTemplatesPath = (query: RoleTemplateListQuery | undefined): string => {
  if (!query?.entityTypeSlug) return '/admin/roles';
  const params = new URLSearchParams({ entityTypeSlug: query.entityTypeSlug });
  return `/admin/roles?${params.toString()}`;
};

const userRolesPath = (userId: string): string =>
  `/admin/users/${encodeURIComponent(userId)}/roles`;

export const createRolesClient = (transport: Transport): RolesClient => ({
  listTemplates: (query, options) =>
    transport.get(buildTemplatesPath(query), RoleTemplateListResponseSchema, options),
  listForUser: (userId, options) =>
    transport.get(userRolesPath(userId), UserRolesResponseSchema, options),
  assign: (userId, body, options) =>
    transport.post(userRolesPath(userId), UserRolesResponseSchema, body, options),
  unassign: (userId, roleTemplateId, options) =>
    transport.delete(
      `${userRolesPath(userId)}/${encodeURIComponent(roleTemplateId)}`,
      UserRolesResponseSchema,
      options,
    ),
});
